import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu, Loader2,
  LayoutDashboard, BarChart3, TrendingUp, DollarSign, Mic,
  ChevronRight, Filter, Workflow, ArrowRight, Activity, Store,
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  { value: 'all', label: 'All Industries' },
  { value: 'general', label: 'General Business' },
  { value: 'construction', label: 'Construction' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'realestate', label: 'Real Estate' },
  { value: 'legal', label: 'Legal' },
  { value: 'ecommerce', label: 'E-Commerce' },
  { value: 'marketing', label: 'Marketing' },
];

const DemandIntel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [industryFilter, setIndustryFilter] = useState('all');

  useEffect(() => { fetchData(); }, [industryFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = industryFilter && industryFilter !== 'all' ? { industry: industryFilter } : {};
      const [pkgRes, rankRes] = await Promise.all([
        axios.get(`${API}/demand/packages`, { params }),
        axios.get(`${API}/industries/rankings/top`),
      ]);
      setPackages(pkgRes.data);
      setRankings(rankRes.data);
    } catch (err) {
      console.error('Failed to fetch demand data:', err);
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: BarChart3, label: 'Demand Intel', path: '/demand' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: MessageSquare, label: 'Pitch Generator', path: '/pitch' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Activity, label: 'API Hub', path: '/hub' },
  ];

  const getDemandLevel = (score) => {
    if (score >= 80) return { label: 'HIGH', cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' };
    if (score >= 50) return { label: 'MEDIUM', cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30' };
    return { label: 'LOW', cls: 'bg-white/10 text-muted-foreground border-white/10' };
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <HSLogo size={32} />
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}>
                  <Icon className="w-5 h-5" />{item.label}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">{user?.name?.charAt(0) || 'U'}</div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-muted-foreground truncate">{user?.email}</p></div>
            </div>
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              <h1 className="text-lg font-bold" data-testid="demand-title">Demand Intelligence</h1>
            </div>
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="w-44 bg-[#0a0a0a] border-white/10 rounded-sm h-9 text-sm" data-testid="industry-filter">
                <Filter className="w-3.5 h-3.5 mr-1 text-muted-foreground" /><SelectValue placeholder="All Industries" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-white/10">
                {INDUSTRIES.map(ind => <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500/10 to-violet-500/10 border border-blue-500/20 p-6">
            <h2 className="text-xl font-bold mb-2">Service Opportunity Packages</h2>
            <p className="text-muted-foreground text-sm">Clustered from live job market data. Each package represents a standardized, AI-augmented service you can offer to businesses.</p>
          </div>

          {/* High Opportunity Industries */}
          {rankings.length > 0 && (
            <div data-testid="industry-rankings">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> High Opportunity Industries
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {rankings.slice(0, 5).map((ind) => (
                  <button
                    key={ind.id}
                    onClick={() => setIndustryFilter(ind.id)}
                    className={`bg-[#0a0a0a] border p-3 text-left transition-all hover:border-emerald-500/20 ${industryFilter === ind.id ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/5'}`}
                    data-testid={`ranking-${ind.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium truncate">{ind.name}</span>
                      {ind.status === 'spotlight' && <span className="text-[8px] px-1 bg-amber-500/20 text-amber-400">HOT</span>}
                    </div>
                    <div className="flex items-end gap-1">
                      <span className="font-mono text-lg font-bold text-emerald-400">{ind.ranking?.overall_score || 0}</span>
                      <span className="text-[10px] text-muted-foreground mb-0.5">/100</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 mt-2">
                      <div className="text-[9px] text-muted-foreground">Volume<div className="w-full h-0.5 bg-white/5 mt-0.5"><div className="h-full bg-blue-500" style={{width: `${ind.ranking?.listing_volume || 0}%`}} /></div></div>
                      <div className="text-[9px] text-muted-foreground">Automate<div className="w-full h-0.5 bg-white/5 mt-0.5"><div className="h-full bg-violet-500" style={{width: `${ind.ranking?.automation_leverage || 0}%`}} /></div></div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
          ) : packages.length === 0 ? (
            <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-bold mb-2">No demand data yet</h3>
              <p className="text-muted-foreground mb-4">Search for jobs first to generate demand intelligence.</p>
              <Button onClick={() => navigate('/jobs')} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm">Search Jobs</Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map((pkg) => {
                const demand = getDemandLevel(pkg.demand_score);
                return (
                  <div key={pkg.id} className="bg-[#0a0a0a] border border-white/5 hover:border-white/10 transition-all group" data-testid={`demand-package-${pkg.id}`}>
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-sm leading-tight">{pkg.name}</h3>
                          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{pkg.industry}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {pkg.voice_ai && (
                            <span className="px-1.5 py-0.5 bg-violet-500/20 text-violet-400 text-[10px] font-mono flex items-center gap-1">
                              <Mic className="w-2.5 h-2.5" /> VOICE
                            </span>
                          )}
                          <span className={`px-1.5 py-0.5 text-[10px] font-mono border ${demand.cls}`}>{demand.label}</span>
                        </div>
                      </div>

                      {/* Demand Score Bar */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                          <span>DEMAND SCORE</span>
                          <span className="font-mono">{pkg.demand_score}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-white/5">
                          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pkg.demand_score}%` }} />
                        </div>
                      </div>

                      {/* Pricing */}
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        <div className="bg-black/30 p-2 text-center">
                          <p className="text-[10px] text-muted-foreground uppercase">Low</p>
                          <p className="font-mono text-sm text-white">${pkg.fractional_pricing?.low?.toLocaleString()}</p>
                        </div>
                        <div className="bg-emerald-500/5 border border-emerald-500/10 p-2 text-center">
                          <p className="text-[10px] text-emerald-400 uppercase">Mid</p>
                          <p className="font-mono text-sm text-emerald-400">${pkg.fractional_pricing?.mid?.toLocaleString()}</p>
                        </div>
                        <div className="bg-black/30 p-2 text-center">
                          <p className="text-[10px] text-muted-foreground uppercase">High</p>
                          <p className="font-mono text-sm text-white">${pkg.fractional_pricing?.high?.toLocaleString()}</p>
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground text-center mb-3">per client / month</p>

                      {/* Market Salary */}
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-3 py-2 border-t border-white/5">
                        <span>Market salary equiv.</span>
                        <span className="font-mono">${(pkg.market_salary_range?.min || 0).toLocaleString()} - ${(pkg.market_salary_range?.max || 0).toLocaleString()}/yr</span>
                      </div>

                      {/* Example titles */}
                      {pkg.common_titles?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-[10px] text-muted-foreground uppercase mb-1">Common Titles</p>
                          <div className="flex flex-wrap gap-1">
                            {pkg.common_titles.slice(0, 3).map(t => (
                              <span key={t} className="text-[10px] px-1.5 py-0.5 bg-white/5 text-muted-foreground truncate max-w-[150px]">{t}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Pitch angle */}
                      <p className="text-xs text-muted-foreground italic border-t border-white/5 pt-3">"{pkg.pitch_angle}"</p>
                    </div>

                    {/* Actions */}
                    <div className="border-t border-white/5 p-3 flex gap-2">
                      <Button onClick={() => navigate('/workflow')} variant="outline" size="sm" className="flex-1 border-white/10 text-xs rounded-sm h-8">
                        Build Workflow <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                      <Button onClick={() => navigate('/jobs')} size="sm" className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 text-xs font-bold rounded-sm h-8">
                        Find Jobs <Search className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DemandIntel;
